import React from "react";
import { UseFormRegisterReturn, FieldError } from "react-hook-form";
import { Tooltip } from "react-tooltip";

interface FormTextAreaProps {
  label?: string;
  description?: string;
  rows?: number;
  maxLength?: number;
  showCounter?: boolean;
  placeholder?: string;
  error?: FieldError;
  value?: string;
  className?: string;
  register?: UseFormRegisterReturn;
  tooltip?: string;
  /** Conteúdo exibido entre a descrição e o campo (ex.: contexto da pergunta). */
  complemento?: React.ReactNode;
}

export function FormTextArea({
  label,
  description,
  rows = 4,
  maxLength,
  showCounter = false,
  placeholder,
  error,
  value = "",
  className = "",
  register,
  tooltip,
  complemento,
}: FormTextAreaProps) {
  const fieldId = register?.name ? `field-${register.name}` : label ? `field-${label.replace(/\s+/g, "-")}` : undefined;
  const errorId = fieldId ? `${fieldId}-error` : undefined;
  const descriptionId = fieldId ? `${fieldId}-desc` : undefined;
  const tooltipId = label ? `tooltip-${label.replace(/\s+/g, "-")}` : undefined;

  const describedBy = [
    description ? descriptionId : null,
    error ? errorId : null,
  ].filter(Boolean).join(" ") || undefined;

  return (
    <div className={className}>
      {label && (
        <label htmlFor={fieldId} className="text-lg sm:text-xl font-bold text-gray-bb-1 mb-3 sm:mb-4 flex items-center gap-1.5">
          {label}
          {tooltip && tooltipId && (
            <>
              <span
                data-tooltip-id={tooltipId}
                className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-bb text-white-bb text-xs font-bold cursor-help shrink-0"
                aria-label="Ajuda"
              >
                ?
              </span>
              <Tooltip
                id={tooltipId}
                place="top"
                className="!max-w-xs !whitespace-pre-line !z-50"
              >
                {tooltip}
              </Tooltip>
            </>
          )}
        </label>
      )}
      {description && (
        <p id={descriptionId} className="text-gray-bb-2 text-sm sm:text-base mb-4 sm:mb-6 font-bold"
           dangerouslySetInnerHTML={{ __html: description }}
        />
      )}
      {complemento}

      <textarea
        {...register}
        id={fieldId}
        rows={rows}
        maxLength={maxLength}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className={`w-full border rounded-lg p-2.5 sm:p-3 text-sm sm:text-base focus:ring-2 focus:ring-blue-bb focus:border-transparent resize-none transition-all ${
          error ? "border-red-500" : "border-gray-bb-4"
        }`}
        placeholder={placeholder}
      />

      {showCounter && maxLength && (
        <div className="text-right text-xs sm:text-sm text-gray-bb-2 mt-2" aria-live="polite">
          Caracteres: {value?.length || 0}/{maxLength}
        </div>
      )}
      {error && (
        <p id={errorId} role="alert" className="text-red-500 text-xs sm:text-sm mt-1">{error.message}</p>
      )}
    </div>
  );
}
